import type { UserProfile } from '@/lib/types'
import { cn, initialsOf, avatarColor, profileName } from '@/lib/utils'

interface Props {
  /** Profile of the user; may be undefined while profiles load. */
  profile: UserProfile | undefined
  /** User id — drives the fallback color and is always available. */
  userId: string
  /** Tailwind size classes, e.g. "h-6 w-6". */
  className?: string
  /** Initials text size, e.g. "text-[9px]". */
  textClassName?: string
  /** Suppress the native `title` tooltip — set by callers that already
   *  render their own richer popover on hover (e.g. AvatarStack) so the
   *  two don't double up. */
  noTitle?: boolean
}

/** Round avatar — profile photo if set, else colored initials (Trello-style). */
export function Avatar({
  profile,
  userId,
  className,
  textClassName,
  noTitle = false,
}: Props) {
  const name = profileName(profile)
  const url = profile?.avatar_url ?? null
  return (
    <span
      title={noTitle ? undefined : name || undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full ring-1 ring-[var(--color-border-soft)]',
        !url && avatarColor(userId),
        className ?? 'h-6 w-6',
      )}
    >
      {url ? (
        <img src={url} alt={name} className="h-full w-full object-cover" />
      ) : (
        <span className={cn('font-semibold leading-none', textClassName ?? 'text-[9px]')}>
          {initialsOf(name)}
        </span>
      )}
    </span>
  )
}
